import { useCallback, useMemo, useState } from 'react';
import {
  applyProjectDiagnostics,
  scanProjectDiagnostics,
} from '../../api/client';
import { scanRouteDiagnosticsClient } from '../../lib/routeDiagnostics';
import { useEditorStore } from '../../store/editorStore';
import type {
  DiagnosticIssue,
  DiagnosticReport,
  DiagnosticSeverity,
} from '../../types/diagnostics';
import type { ProjectDocument } from '../../types/project';

function severityBadge(severity: DiagnosticSeverity): string {
  switch (severity) {
    case 'error':
      return 'badge-danger-on-dark';
    case 'warning':
      return 'badge-warning-on-dark';
    case 'info':
      return 'bg-sky-500/20 text-sky-200';
  }
}

function summaryLine(summary: DiagnosticReport['summary']): string {
  const parts: string[] = [];
  if (summary.duplicate_routes > 0) {
    parts.push(
      `${summary.duplicate_routes} duplicate route(s) across ${summary.duplicate_groups} group(s)`,
    );
  }
  if (summary.zero_length_groups > 0) {
    parts.push(`${summary.zero_length_groups} zero-length group(s)`);
  }
  if (summary.dangling_routes > 0) {
    parts.push(`${summary.dangling_routes} dangling route(s)`);
  }
  if (summary.isolated_nodes > 0) {
    parts.push(`${summary.isolated_nodes} isolated node(s)`);
  }
  if (parts.length === 0) return 'No issues found.';
  const safe =
    summary.auto_fixable_count > 0
      ? ` — ${summary.auto_fixable_count} safe to auto-fix`
      : '';
  return parts.join(' · ') + safe;
}

function IssueRow({
  issue,
  readOnly,
  onOpenNode,
  onSelectRoute,
  onDeleteRoutes,
}: {
  issue: DiagnosticIssue;
  readOnly: boolean;
  onOpenNode: (nodeId: string) => void;
  onSelectRoute: (routeId: string) => void;
  onDeleteRoutes: (routeIds: string[], keepRouteId?: string) => void;
}) {
  return (
    <li className="rounded-lg border border-border bg-surface-overlay/40 px-3 py-2 text-xs">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${severityBadge(issue.severity)}`}
            >
              {issue.severity}
            </span>
            <span className="font-mono text-[10px] text-text-subtle">
              {issue.code}
            </span>
          </div>
          <p className="text-text">{issue.message}</p>
          {(issue.route_names.length > 0 || issue.node_names.length > 0) && (
            <p className="text-text-subtle">
              {issue.route_names.length > 0 && (
                <span>Routes: {issue.route_names.join(', ')}</span>
              )}
              {issue.route_names.length > 0 && issue.node_names.length > 0 && (
                <span> · </span>
              )}
              {issue.node_names.length > 0 && (
                <span>Nodes: {issue.node_names.join(', ')}</span>
              )}
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap gap-1">
          {issue.suggested_action === 'open_node' && issue.node_ids[0] && (
            <button
              type="button"
              className="btn-ghost px-2 py-1 text-[10px]"
              onClick={() => onOpenNode(issue.node_ids[0])}
            >
              Open node
            </button>
          )}
          {issue.route_ids[0] && issue.suggested_action !== 'open_node' && (
            <button
              type="button"
              className="btn-ghost px-2 py-1 text-[10px]"
              onClick={() => onSelectRoute(issue.route_ids[0])}
            >
              Select route
            </button>
          )}
          {!readOnly &&
            issue.route_ids.length > 0 &&
            (issue.suggested_action === 'delete_route' ||
              issue.suggested_action === 'delete_duplicate_keep_first') && (
              <button
                type="button"
                className="btn-danger-ghost px-2 py-1 text-[10px]"
                onClick={() =>
                  onDeleteRoutes(issue.route_ids, issue.keep_route_id)
                }
              >
                Delete route{issue.route_ids.length > 1 ? 's' : ''}
              </button>
            )}
        </div>
      </div>
    </li>
  );
}

export default function ProjectDiagnosticsPanel({
  readOnly,
}: {
  readOnly: boolean;
}) {
  const projectId = useEditorStore((s) => s.projectId);
  const document = useEditorStore((s) => s.document);
  const openNodeEdit = useEditorStore((s) => s.openNodeEdit);
  const selectRoute = useEditorStore((s) => s.selectRoute);
  const setDocument = useEditorStore((s) => s.setDocument);
  const markSaved = useEditorStore((s) => s.markSaved);

  const [report, setReport] = useState<DiagnosticReport | null>(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usedServer, setUsedServer] = useState(false);

  const clientPreview = useMemo(() => {
    if (!document) return null;
    return scanRouteDiagnosticsClient(document);
  }, [document]);

  const displayReport = report ?? clientPreview;

  const applyDocument = useCallback(
    (doc: Record<string, unknown>, diagnostics: DiagnosticReport) => {
      setDocument(doc as unknown as ProjectDocument);
      markSaved();
      setReport(diagnostics);
      setUsedServer(true);
    },
    [markSaved, setDocument],
  );

  const handleScan = async () => {
    if (!projectId) return;
    setScanning(true);
    setError(null);
    try {
      const serverReport = await scanProjectDiagnostics(projectId);
      setReport(serverReport);
      setUsedServer(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scan failed');
      if (document) {
        setReport(scanRouteDiagnosticsClient(document));
        setUsedServer(false);
      }
    } finally {
      setScanning(false);
    }
  };

  const handleDeleteRoutes = async (
    routeIds: string[],
    keepRouteId?: string,
  ) => {
    if (!projectId || readOnly || routeIds.length === 0) return;
    setApplying(true);
    setError(null);
    try {
      const res = await applyProjectDiagnostics(projectId, {
        actions: [
          {
            type: keepRouteId
              ? 'delete_duplicate_keep_first'
              : 'delete_routes',
            route_ids: routeIds,
            keep_route_id: keepRouteId,
          },
        ],
      });
      applyDocument(res.document, res.diagnostics);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setApplying(false);
    }
  };

  const handleFixAllSafe = async () => {
    if (!projectId || readOnly) return;
    setApplying(true);
    setError(null);
    try {
      const res = await applyProjectDiagnostics(projectId, {
        fix_all_safe: true,
      });
      applyDocument(res.document, res.diagnostics);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const autoFixable =
    displayReport?.issues.filter((i) => i.auto_fixable).length ?? 0;

  return (
    <div className="flex min-h-0 flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-text">Project diagnostics</h3>
          <p className="text-[11px] text-text-subtle">
            Find duplicate routes, zero-length paths, and dangling connections.
            {!usedServer && displayReport && (
              <span className="text-warning-on-dark/90"> Preview (scan for server results).</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary px-3 py-1.5 text-xs"
            onClick={() => void handleScan()}
            disabled={scanning || !projectId}
          >
            {scanning ? 'Scanning…' : 'Scan project'}
          </button>
          {!readOnly && autoFixable > 0 && (
            <button
              type="button"
              className="btn-primary px-3 py-1.5 text-xs"
              onClick={() => void handleFixAllSafe()}
              disabled={applying || !projectId}
            >
              {applying ? 'Applying…' : 'Fix all safe issues'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert-danger px-3 py-2 text-xs">
          {error}
        </div>
      )}

      {displayReport && (
        <p className="text-[11px] text-text-muted">
          {summaryLine(displayReport.summary)}
        </p>
      )}

      {displayReport && displayReport.issues.length === 0 && (
        <p className="text-xs text-text-subtle">
          No issues detected. Click Scan project to refresh from the server.
        </p>
      )}

      {displayReport && displayReport.issues.length > 0 && (
        <ul className="max-h-48 space-y-2 overflow-y-auto">
          {displayReport.issues.map((issue, index) => (
            <IssueRow
              key={`${issue.code}-${index}`}
              issue={issue}
              readOnly={readOnly}
              onOpenNode={openNodeEdit}
              onSelectRoute={(routeId) => selectRoute(routeId, { focus: true })}
              onDeleteRoutes={(ids, keep) => void handleDeleteRoutes(ids, keep)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
